import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, CheckCircle, AlertCircle, Upload, Trash2, FileText, ChevronRight, ChevronLeft
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { isUserCoach } from '../../../lib/auth';
import { supabase } from '../../../lib/supabase';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  fetchCoachConfig,
  saveCloneSetup,
  createVectorStore,
  uploadKnowledgeFile,
  listKnowledgeFiles,
  deleteKnowledgeFile,
  type CloneSetupData,
  type KnowledgeFile,
} from '../../../lib/ai-coach';

const STEPS = [
  { id: 'name', title: 'Ad ve Amac', description: 'YZ Kocunuz nasil anilacak?' },
  { id: 'style', title: 'Kocluk Tarzi', description: 'YZ Kocunuz nasil iletisim kuracak?' },
  { id: 'principles', title: 'Ilkeler', description: 'Temel kocluk metodolojiniz' },
  { id: 'boundaries', title: 'Sinirlar', description: 'YZ Kocunuzun kacinmasi gerekenler' },
  { id: 'welcome', title: 'Karsilama', description: 'Ilk izlenim onemlidir' },
  { id: 'knowledge', title: 'Bilgi Tabani', description: 'Materyallerinizi yukleyin' },
  { id: 'access', title: 'Erisim ve Onay', description: 'YZ Kocunuzu kimler kullanabilir?' },
];

export function AICoachSetupWizardTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [formData, setFormData] = useState<CloneSetupData>({
    cloneName: '',
    cloneDescription: '',
    coachingStyle: '',
    idealClient: '',
    principles: '',
    dosAndDonts: '',
    disclaimers: '',
    welcomeMessage: 'Hos geldiniz! Bugun size nasil yardimci olabilirim?',
    starterQuestions: ['', '', ''],
    clientAccessEnabled: false,
  });

  const [knowledgeFiles, setKnowledgeFiles] = useState<KnowledgeFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingStore, setIsCreatingStore] = useState(false);
  const [hasVectorStore, setHasVectorStore] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/tr/login'); return; }

    const init = async () => {
      try {
        const coach = await isUserCoach(user.id);
        if (!coach) { navigate('/tr/login'); return; }

        const { data: coachRecord } = await supabase
          .from('coaches').select('id').eq('user_id', user.id).maybeSingle();
        if (!coachRecord) { navigate('/tr/login'); return; }
        setCoachId(coachRecord.id);

        const config = await fetchCoachConfig(coachRecord.id);
        if (config) {
          setFormData({
            cloneName: config.cloneName,
            cloneDescription: config.cloneDescription,
            coachingStyle: config.coachingStyle,
            idealClient: config.idealClient,
            principles: config.principles,
            dosAndDonts: config.dosAndDonts,
            disclaimers: config.disclaimers,
            welcomeMessage: config.welcomeMessage || 'Hos geldiniz! Bugun size nasil yardimci olabilirim?',
            starterQuestions: config.starterQuestions.length > 0 ? config.starterQuestions : ['', '', ''],
            clientAccessEnabled: config.clientAccessEnabled,
          });
          setHasVectorStore(!!config.vectorStoreId);
        }

        try {
          const files = await listKnowledgeFiles();
          setKnowledgeFiles(files);
        } catch (_) { /* henuz dosya yok */ }
      } catch (err) {
        console.error('Error loading setup:', err);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [user, authLoading, navigate]);

  const updateField = (field: keyof CloneSetupData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateVectorStore = async () => {
    if (!coachId) return;
    setIsCreatingStore(true);
    try {
      await createVectorStore(coachId);
      setHasVectorStore(true);
      setToast({ type: 'success', message: 'Bilgi tabani basariyla olusturuldu' });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setIsCreatingStore(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      await uploadKnowledgeFile(file);
      const files = await listKnowledgeFiles();
      setKnowledgeFiles(files);
      setToast({ type: 'success', message: `Yuklendi: ${file.name}` });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteKnowledgeFile(fileId);
      setKnowledgeFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const handleSave = async () => {
    if (!coachId) return;
    setIsSaving(true);
    try {
      const cleanedData = {
        ...formData,
        starterQuestions: formData.starterQuestions.filter(q => q.trim() !== ''),
      };
      await saveCloneSetup(coachId, cleanedData);
      setToast({ type: 'success', message: 'YZ Koc Klonu basariyla kaydedildi!' });
      setTimeout(() => navigate('/tr/coach/ai-settings'), 1500);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const canProceed = () => {
    if (currentStep === 0) return formData.cloneName.trim().length > 0;
    return true;
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Kurulum sihirbazi yukleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">YZ Koc Klonu Kurulumu</h1>
              <p className="freud-text-sm text-chocolate-500">
                Adim {currentStep + 1} / {STEPS.length}: {STEPS[currentStep].description}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Ilerleme cubugu */}
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <div className="flex gap-1.5">
          {STEPS.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(idx)}
              className={`flex-1 h-2 rounded-full transition-all ${
                idx <= currentStep ? 'bg-sage-500' : 'bg-cream-300'
              }`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-2">
          {STEPS.map((step, idx) => (
            <span key={step.id} className={`text-[10px] font-medium ${
              idx === currentStep ? 'text-sage-700' : 'text-chocolate-400'
            }`}>
              {step.title}
            </span>
          ))}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 p-4 rounded-xl mb-6 ${
              toast.type === 'success' ? 'bg-sage-100 text-sage-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <p className="text-sm font-medium">{toast.message}</p>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="freud-card-elevated p-8"
          >
            {/* Adim 0: Ad ve Amac */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    YZ Koc Klonu Adi *
                  </label>
                  <input
                    type="text"
                    value={formData.cloneName}
                    onChange={(e) => updateField('cloneName', e.target.value)}
                    placeholder="orn. Koc Ayse, Zihniyet Mentoru, Buyume Rehberi"
                    className="freud-input text-lg"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">YZ Kocunuz kendini bu isimle tanitacak</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    YZ Kocunuz ne konuda yardimci oluyor?
                  </label>
                  <textarea
                    value={formData.cloneDescription}
                    onChange={(e) => updateField('cloneDescription', e.target.value)}
                    placeholder="orn. Profesyonellerin zihniyet koçlugu, sorumluluk ve kisisel gelisim stratejileri araciligiyla tam potansiyellerini ortaya cikarmalarına yardimci oluyorum."
                    rows={4}
                    className="freud-input resize-none"
                  />
                </div>
              </div>
            )}

            {/* Adim 1: Kocluk Tarzi */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Kocluk Tarzi ve Ses Tonu
                  </label>
                  <textarea
                    value={formData.coachingStyle}
                    onChange={(e) => updateField('coachingStyle', e.target.value)}
                    placeholder="orn. Sicak ve cesaretlendirici ama dogrudan. Icgoruyu kismak icin sorular kullanir. Meydan okuma ile destegi dengeler. Sohbet dilinde konusur, jargondan kacinir."
                    rows={5}
                    className="freud-input resize-none"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">YZ Kocunuzun nasil iletisim kurmasi gerektigini aciklayin</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Ideal Musteri Profili
                  </label>
                  <textarea
                    value={formData.idealClient}
                    onChange={(e) => updateField('idealClient', e.target.value)}
                    placeholder="orn. Kariyerlerini ilerletmek, liderlik becerilerini gelistirmek veya buyuk yasam gecislerinde yol bulmak isteyen 30-50 yas arasi profesyoneller."
                    rows={3}
                    className="freud-input resize-none"
                  />
                </div>
              </div>
            )}

            {/* Adim 2: Ilkeler */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Temel Kocluk Ilkeleri ve Metodolojisi
                  </label>
                  <textarea
                    value={formData.principles}
                    onChange={(e) => updateField('principles', e.target.value)}
                    placeholder={"orn.\n- Sabit zihniyete karsin buyume zihniyeti\n- Kucuk tutarli eylemler birikerek buyur\n- Oz farkindalik degisimin temelidir\n- Sinirlandirici inanclara kanit gostererek meydan oku\n- Sonuclari degil, ilerlemeyi kutla"}
                    rows={8}
                    className="freud-input resize-none"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">Koclugunuza rehberlik eden temel inanc ve yontemlerinizi listeleyin</p>
                </div>
              </div>
            )}

            {/* Adim 3: Sinirlar */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Yapilacaklar ve Yapilmayacaklar
                  </label>
                  <textarea
                    value={formData.dosAndDonts}
                    onChange={(e) => updateField('dosAndDonts', e.target.value)}
                    placeholder={"orn.\nYAPILACAKLAR: Guclu sorular sor, basarilari kutla, uygulanabilir adimlar oner\nYAPILMAYACAKLAR: Tibbi tavsiye verme, teshis koyma, sonuclar hakkinda sozler verme, diger musterilerden bahsetme"}
                    rows={5}
                    className="freud-input resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Yasal Uyarilar ve Sinirlar
                  </label>
                  <textarea
                    value={formData.disclaimers}
                    onChange={(e) => updateField('disclaimers', e.target.value)}
                    placeholder="orn. Bu YZ Kocu yalnizca kisisel gelisim amaclidir. Profesyonel terapi, danismanlik veya tibbi tavsiyenin yerini tutmaz. Kriz durumundaysaniz lutfen nitelikli bir uzmanla iletisime gecin."
                    rows={4}
                    className="freud-input resize-none"
                  />
                </div>
              </div>
            )}

            {/* Adim 4: Karsilama ve Baslangic */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Karsilama Mesaji
                  </label>
                  <textarea
                    value={formData.welcomeMessage}
                    onChange={(e) => updateField('welcomeMessage', e.target.value)}
                    placeholder="orn. Merhaba! Ben kisisel YZ Kocunuzum. Potansiyelinizi kesmek ve hedeflerinize dogru calisman icin buradayim. Bugun ne uzerine odaklanmak istersiniz?"
                    rows={3}
                    className="freud-input resize-none"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">Kullanicilarin sohbeti actiktan sonra gordugu ilk mesaj</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Onerilen Baslangic Sorulari
                  </label>
                  <p className="text-xs text-chocolate-400 mb-3">Kullanicilara sohbeti nasil baslatacaklari konusunda fikir verin</p>
                  {formData.starterQuestions.map((q, idx) => (
                    <input
                      key={idx}
                      type="text"
                      value={q}
                      onChange={(e) => {
                        const updated = [...formData.starterQuestions];
                        updated[idx] = e.target.value;
                        updateField('starterQuestions', updated);
                      }}
                      placeholder={`Baslangic sorusu ${idx + 1}`}
                      className="freud-input mb-2"
                    />
                  ))}
                  {formData.starterQuestions.length < 6 && (
                    <button
                      onClick={() => updateField('starterQuestions', [...formData.starterQuestions, ''])}
                      className="text-sm text-sage-600 hover:text-sage-700 font-medium"
                    >
                      + Baska soru ekle
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Adim 5: Bilgi Tabani */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <p className="text-sm text-chocolate-600">
                  YZ Kocunuzun referans almasi icin belgeler, cerceveler veya egitim materyalleri yukleyin.
                  Desteklenen formatlar: PDF, TXT, DOCX, MD.
                </p>

                {!hasVectorStore ? (
                  <div className="text-center py-8 border-2 border-dashed border-cream-300 rounded-xl">
                    <FileText className="w-10 h-10 text-cream-400 mx-auto mb-3" />
                    <p className="text-sm text-chocolate-500 mb-4">Dosya yuklemek icin once bilgi tabanini olusturun</p>
                    <button
                      onClick={handleCreateVectorStore}
                      disabled={isCreatingStore}
                      className="freud-btn-primary"
                    >
                      {isCreatingStore ? 'Olusturuluyor...' : 'Bilgi Tabani Olustur'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="border-2 border-dashed border-cream-300 rounded-xl p-6 text-center">
                      <Upload className="w-8 h-8 text-cream-400 mx-auto mb-2" />
                      <label className="cursor-pointer">
                        <span className="text-sm text-sage-600 font-semibold hover:text-sage-700">
                          {isUploading ? 'Yukleniyor...' : 'Dosya yuklemek icin tiklayin'}
                        </span>
                        <input
                          type="file"
                          onChange={handleFileUpload}
                          disabled={isUploading}
                          accept=".pdf,.txt,.docx,.md,.csv"
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-chocolate-400 mt-1">Dosya basina maksimum 50MB</p>
                    </div>

                    {knowledgeFiles.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-chocolate-700">Yuklenen Dosyalar</h4>
                        {knowledgeFiles.map(file => (
                          <div key={file.id} className="flex items-center justify-between py-2.5 px-4 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-chocolate-400" />
                              <div>
                                <p className="text-sm font-medium text-chocolate-700">{file.filename}</p>
                                <p className="text-xs text-chocolate-400">
                                  {(file.file_size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteFile(file.id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Adim 6: Erisim ve Onay */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-cream-50 rounded-xl border border-cream-200">
                  <div>
                    <p className="text-sm font-semibold text-chocolate-800">Musteri Erisimi</p>
                    <p className="text-xs text-chocolate-500">Katilimcilarinizin/musterilerinizin YZ Kocunuzla sohbet etmesine izin verin</p>
                  </div>
                  <button
                    onClick={() => updateField('clientAccessEnabled', !formData.clientAccessEnabled)}
                    className="group flex items-center"
                  >
                    <div className={`relative w-14 h-7 rounded-full transition-colors ${
                      formData.clientAccessEnabled ? 'bg-sage-500' : 'bg-gray-300'
                    }`}>
                      <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                        formData.clientAccessEnabled ? 'translate-x-[30px]' : 'translate-x-0.5'
                      }`} />
                    </div>
                  </button>
                </div>

                <div className="p-4 bg-sage-50 rounded-xl border border-sage-200">
                  <h4 className="text-sm font-semibold text-sage-800 mb-3">Kurulum Ozeti</h4>
                  <div className="space-y-2 text-sm text-sage-700">
                    <p><span className="font-medium">Ad:</span> {formData.cloneName || '(belirlenmedi)'}</p>
                    <p><span className="font-medium">Amac:</span> {formData.cloneDescription ? formData.cloneDescription.slice(0, 80) + '...' : '(belirlenmedi)'}</p>
                    <p><span className="font-medium">Tarzi:</span> {formData.coachingStyle ? 'Yapilandirildi' : '(belirlenmedi)'}</p>
                    <p><span className="font-medium">Ilkeler:</span> {formData.principles ? 'Yapilandirildi' : '(belirlenmedi)'}</p>
                    <p><span className="font-medium">Sinirlar:</span> {formData.dosAndDonts || formData.disclaimers ? 'Yapilandirildi' : '(belirlenmedi)'}</p>
                    <p><span className="font-medium">Bilgi dosyalari:</span> {knowledgeFiles.length} yuklendi</p>
                    <p><span className="font-medium">Musteri erisimi:</span> {formData.clientAccessEnabled ? 'Acik' : 'Kapali'}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigasyon */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
            disabled={currentStep === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-chocolate-600 font-semibold rounded-xl hover:bg-cream-100 transition-all disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
            Geri
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-sage-600 text-white font-semibold rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50"
            >
              Ileri
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={isSaving || !formData.cloneName.trim()}
              className="inline-flex items-center gap-2 px-8 py-2.5 bg-sage-600 text-white font-semibold rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50"
            >
              {isSaving ? 'Kaydediliyor...' : 'Kaydet ve Etkinlestir'}
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
